import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

interface TabItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  requiredPermissions?: string[];
}

export const MobileNavigation: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();

  const tabs: TabItem[] = [
    {
      id: 'dashboard',
      label: t('nav.dashboard'),
      icon: (
        <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6a2 2 0 01-2 2H10a2 2 0 01-2-2V5z" />
        </svg>
      ),
      path: '/dashboard'
    },
    {
      id: 'reportes',
      label: t('nav.reports'),
      icon: (
        <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/reportes',
      requiredPermissions: ['reportes.ver']
    },
    {
      id: 'crear',
      label: t('common.create'),
      icon: (
        <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      path: '/reportes/crear',
      requiredPermissions: ['reportes.crear']
    },
    {
      id: 'admin',
      label: t('nav.administration'),
      icon: (
        <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      path: '/admin'
    }
  ];

  // Filtrar tabs basado en permisos
  const visibleTabs = tabs.filter(tab => {
    if (!tab.requiredPermissions) return true;
    return tab.requiredPermissions.every(permission => tienePermiso(permission));
  });

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleTabPress = (path: string) => {
    navigate(path);
  };

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[9999] safe-area-bottom">
      <div className="flex justify-around items-center h-16">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabPress(tab.path)}
            className={`flex-1 max-w-[20%] h-full flex flex-col items-center justify-center transition-colors duration-200 ${
              isActive(tab.path)
                ? 'text-green-600 bg-green-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex flex-col items-center space-y-1">
              <div className={`transition-colors duration-200 ${
                isActive(tab.path) ? 'text-green-600' : 'text-gray-400'
              }`}>
                {tab.icon}
              </div>
              <span className="text-xs font-medium leading-tight text-center">
                {tab.label}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MobileNavigation;